// Public read-only view of a shared plant list. No auth required.
// Recipient sees: owner's first name, list of plant cards, badges, and (for
// view_mode=full) companion plants, pests, care notes when the card is opened.
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, ShieldAlert, Sprout, Sun, CloudSun, Moon, ChevronLeft, ExternalLink } from "lucide-react";
import { shares as sharesApi } from "@/lib/api";
import { idNative, idEndangered, idStateNote } from "@/lib/idCompat";
import { stateCode, STATE_CODES } from "@/lib/usStates";
import { normalizeSunPreference } from "@/lib/sunPref";

const isUnidentified = (item) => {
  const id = item?.identification || {};
  const name = (id.common_name || "").trim().toLowerCase();
  return item?.needs_revisit === true || !name || name === "unknown" || name === "desconocido";
};

const SunIcon = ({ pref }) => {
  const p = (pref || []).join(",").toLowerCase();
  if (p.includes("full sun") && !p.includes("shade")) return <Sun className="w-3.5 h-3.5" strokeWidth={1.5} />;
  if (p.includes("shade") && !p.includes("sun")) return <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />;
  return <CloudSun className="w-3.5 h-3.5" strokeWidth={1.5} />;
};

export default function Share() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState({
    loading: true, error: null, data: null,
    items: [], hasMore: false, offset: 0, loadingMore: false,
  });
  const [expanded, setExpanded] = useState(null); // scan_id currently open

  const PAGE_SIZE = 50;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await sharesApi.publicGet(token, { offset: 0, limit: PAGE_SIZE });
        if (!cancelled) {
          const items = data.items || [];
          setState({
            loading: false,
            error: null,
            data,
            items,
            hasMore: !!data.has_more,
            offset: items.length,
            loadingMore: false,
          });
          // Single-plant shares (e.g. the QR code baked into a printed info
          // card) skip straight to the expanded view — the collapsed card is
          // not intuitive when there's only one thing to look at. Multi-plant
          // lists still start collapsed so the reader can scan the whole
          // gallery first.
          if (items.length === 1) setExpanded(items[0].scan_id);
        }
      } catch (err) {
        if (!cancelled) {
          const code = err?.response?.status;
          setState({
            loading: false,
            data: null,
            error: code === 410 ? "expired" : "notfound",
            items: [], hasMore: false, offset: 0, loadingMore: false,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const loadMore = async () => {
    if (state.loadingMore || !state.hasMore) return;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const { data } = await sharesApi.publicGet(token, { offset: state.offset, limit: PAGE_SIZE });
      const newItems = data.items || [];
      setState((s) => ({
        ...s,
        items: [...s.items, ...newItems],
        hasMore: !!data.has_more,
        offset: s.offset + newItems.length,
        loadingMore: false,
      }));
    } catch {
      // Silent failure — keep existing items visible; the load-more button
      // stays clickable so the user can retry manually.
      setState((s) => ({ ...s, loadingMore: false }));
    }
  };

  const viewAll = async () => {
    if (state.loadingMore || !state.hasMore) return;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      // `?all=1` returns up to 300 items in a single response — bounded so
      // a runaway share can't reintroduce the Cloudflare 520 we just fixed.
      const { data } = await sharesApi.publicGet(token, { all: 1 });
      const allItems = data.items || [];
      setState((s) => ({
        ...s,
        items: allItems,
        hasMore: !!data.has_more,
        offset: allItems.length,
        loadingMore: false,
      }));
    } catch {
      setState((s) => ({ ...s, loadingMore: false }));
    }
  };

  const { loading, error, data, items, hasMore, loadingMore } = state;

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="share-loading">
        <div className="animate-pulse text-leaf-mist text-sm">{t("share.loading")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6 text-center" data-testid={`share-error-${error}`}>
        <Leaf className="w-8 h-8 text-leaf mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-3xl text-leaf-ink">
          {error === "expired" ? t("share.expiredTitle") : t("share.notFoundTitle")}
        </h1>
        <p className="text-leaf-mist mt-3 max-w-sm">
          {error === "expired" ? t("share.expiredDesc") : t("share.notFoundDesc")}
        </p>
        <Link to="/login" className="mt-8 rounded-full px-6 py-3 bg-leaf text-white hover:bg-leaf-deep transition text-sm">
          {t("share.tryPrairie")}
        </Link>
      </div>
    );
  }

  const owner = data?.owner_name;
  const state_name = data?.owner_state;
  const isFull = data?.view_mode === "full";
  const heading = data?.title || t("share.defaultHeading", { name: owner || t("share.someone"), state: state_name || "" });

  return (
    <div className="min-h-screen bg-cream" data-testid="share-page">
      <header className="bg-leaf-ink text-white px-5 py-5 flex items-center gap-3">
        <Leaf className="w-6 h-6" strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-lg leading-tight truncate" data-testid="share-owner">{heading}</h1>
          <p className="text-[11px] text-cream/70 mt-0.5">
            {t("share.countLine", { n: data?.count || 0 })}
            {state_name ? ` · ${state_name}` : ""}
          </p>
        </div>
        <Link
          to="/login"
          className="text-[11px] uppercase tracking-[0.14em] rounded-full px-3 py-1.5 bg-white/10 hover:bg-white/20 transition"
          data-testid="share-cta-login"
        >
          {t("share.getPrairie")}
        </Link>
      </header>

      <main className="max-w-[480px] md:max-w-2xl mx-auto px-5 py-6 space-y-3">
        {hasMore && items.length > 0 && (
          <div className="flex justify-end -mt-1 mb-1">
            <button
              type="button"
              onClick={viewAll}
              disabled={loadingMore}
              data-testid="share-view-all"
              className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist hover:text-leaf-ink transition disabled:opacity-60"
            >
              {t("share.viewAll", { count: data?.count || 0 })}
            </button>
          </div>
        )}
        {items.length === 0 && (
          <p className="text-center text-leaf-mist text-sm py-16" data-testid="share-empty">
            {t("share.empty")}
          </p>
        )}
        {items.map((it) => (
          <SharedPlantCard
            key={it.scan_id}
            item={it}
            isFull={isFull}
            fallbackState={state_name}
            open={expanded === it.scan_id}
            onToggle={() => setExpanded(expanded === it.scan_id ? null : it.scan_id)}
            t={t}
          />
        ))}

        {hasMore && (
          <div className="pt-2">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="share-load-more"
              className="w-full rounded-full px-5 py-3 bg-white border border-[#E8E5DF] text-leaf-ink text-sm hover:bg-parchment transition disabled:opacity-60"
            >
              {loadingMore
                ? t("share.loadingMore")
                : t("share.loadMore", { remaining: (data?.count || 0) - items.length })}
            </button>
          </div>
        )}

        {items.length > 0 && !hasMore && (
          <SeeMoreLikeThisCTA state_name={state_name} t={t} />
        )}
      </main>

      <footer className="text-center py-8 text-[11px] text-leaf-mist">
        <p>{t("share.footerLine")}</p>
        <Link to="/about" className="underline mt-1 inline-block">{t("share.aboutLink")}</Link>
      </footer>
    </div>
  );
}

const SeeMoreLikeThisCTA = ({ state_name, t }) => {
  // Show state-specific headline only if the server-provided state matches our
  // canonical US-state list. This is defense-in-depth: even though the server
  // vets the owner's state, we re-check before rendering as text (React escapes
  // by default, so it's safe either way, but this prevents surprising labels
  // from a compromised payload).
  const safeState =
    typeof state_name === "string" && STATE_CODES[state_name] ? state_name : "";
  // The login destination intentionally carries no query params beyond a
  // static analytics tag — no user-supplied state, id, or redirect. This
  // keeps the outbound link free of any URL-driven injection surface.
  const href = "/login?ref=share";
  return (
    <section
      className="mt-6 rounded-3xl bg-gradient-to-br from-leaf-ink to-[#2A3D2D] text-cream p-6 text-center"
      data-testid="share-see-more-cta"
    >
      <p className="font-display text-2xl leading-snug">
        {safeState
          ? t("share.ctaHeadlineState", { state: safeState })
          : t("share.ctaHeadline")}
      </p>
      <p className="mt-2 text-sm text-cream/80 max-w-[36ch] mx-auto">
        {t("share.ctaSubline")}
      </p>
      <Link
        to={href}
        data-testid="share-see-more-button"
        className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-cream text-leaf-ink text-sm font-medium hover:bg-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cream focus-visible:ring-offset-2 focus-visible:ring-offset-leaf-ink"
      >
        {t("share.ctaButton")}
        <ExternalLink className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
      </Link>
    </section>
  );
};

const SharedPlantCard = ({ item, isFull, fallbackState, open, onToggle, t }) => {
  const id = item?.identification || {};
  const sunPrefs = normalizeSunPreference(id.sun_preference);
  const sunPrefLabel = (s) => {
    if (s === "full_sun") return t("result.sunFull");
    if (s === "partial_sun") return t("result.sunPartial");
    if (s === "shade") return t("result.sunShade");
    return s;
  };
  const needsId = isUnidentified(item);
  const carousel = [item.image_base64, ...(item.extra_images || []).map((p) => p.image_base64)].filter(Boolean);

  return (
    <article
      data-testid={`share-plant-${item.scan_id}`}
      className="bg-white rounded-3xl border border-[#E8E5DF] overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="block w-full text-left"
        data-testid={`share-plant-toggle-${item.scan_id}`}
      >
        <div className="flex gap-3 p-3">
          <div className="w-24 h-24 rounded-2xl bg-parchment overflow-hidden flex-shrink-0">
            {item.image_base64 && <img src={item.image_base64} alt={id.common_name || ""} className="w-full h-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg text-leaf-ink leading-tight truncate">
              {needsId ? t("gallery.card.unidentified") : (id.common_name || t("gallery.card.unknown"))}
            </h2>
            {!needsId && id.scientific_name && (
              <p className="italic text-[11px] text-leaf-mist truncate">{id.scientific_name}</p>
            )}
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {idNative(id) === true && (
                <span className="badge-pill bg-[#E6EFE9] text-leaf">
                  {t("gallery.card.stateNative", { code: stateCode(item?.state || fallbackState) })}
                </span>
              )}
              {(id.plant_type || "").toLowerCase() === "weed" && (
                <span className="badge-pill bg-[#FDF0E7] text-rust">{t("gallery.card.weed")}</span>
              )}
              {id.is_keystone && (
                <span className="badge-pill bg-[#E9F0D9] text-[#4A5D23]">{t("gallery.card.keystone")}</span>
              )}
              {idEndangered(id) === true && (
                <span className="badge-pill bg-[#FCE9E4] text-rust inline-flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" strokeWidth={1.5} /> {t("share.badge.endangered")}
                </span>
              )}
              {sunPrefs.length > 0 && (
                <span className="badge-pill bg-parchment text-leaf-ink inline-flex items-center gap-1">
                  <SunIcon pref={sunPrefs} />
                  {sunPrefs.map(sunPrefLabel).join(" · ")}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-[#E8E5DF] p-4 space-y-3" data-testid={`share-plant-details-${item.scan_id}`}>
          {carousel.length > 1 && (
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 snap-x snap-mandatory">
              {carousel.map((src, i) => (
                <img
                  key={`${src.slice(0, 48)}-${i}`}
                  src={src}
                  alt=""
                  className="w-32 h-32 rounded-2xl object-cover flex-shrink-0 snap-center border border-[#E8E5DF]"
                />
              ))}
            </div>
          )}

          {idStateNote(id) && (
            <p className="text-[13px] text-leaf-ink leading-relaxed">
              <span className="font-medium">{t("share.stateStatus")}: </span>{idStateNote(id)}
            </p>
          )}
          {id.is_keystone && (Array.isArray(id.keystone_species_support) ? id.keystone_species_support.length > 0 : String(id.keystone_species_support || "").trim()) && (
            <p className="text-[13px] text-leaf-ink leading-relaxed">
              <Sprout className="w-3.5 h-3.5 inline text-leaf mr-1" strokeWidth={1.5} />
              <span className="font-medium">{t("share.keystoneFor")}: </span>
              {Array.isArray(id.keystone_species_support)
                ? id.keystone_species_support.join(", ")
                : String(id.keystone_species_support)}
            </p>
          )}
          {id.companion_plant?.name && (
            <div className="bg-parchment rounded-2xl p-3">
              <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist">{t("share.companion")}</p>
              <p className="mt-1 text-sm text-leaf-ink"><span className="font-medium">{id.companion_plant.name}</span></p>
              {id.companion_plant.reason && (
                <p className="mt-1 text-[12px] text-leaf-ink/80 leading-relaxed">{id.companion_plant.reason}</p>
              )}
            </div>
          )}
          {isFull && Array.isArray(id.common_pests) && id.common_pests.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist mb-1">{t("share.pests")}</p>
              <ul className="space-y-2">
                {id.common_pests.slice(0, 3).map((p, i) => (
                  <li key={`${p?.name || "pest"}-${i}`} className="rounded-2xl bg-white border border-[#E8E5DF] p-2.5">
                    {p.name && <p className="text-sm font-medium text-leaf-ink">{p.name}</p>}
                    {p.symptoms && <p className="text-[12px] text-leaf-ink/80 mt-0.5">{p.symptoms}</p>}
                    {p.natural_remedy && <p className="text-[12px] text-leaf mt-1">{t("share.pestsRemedy")} {p.natural_remedy}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isFull && Array.isArray(id.medicinal_traditional_uses) && id.medicinal_traditional_uses.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist mb-1">{t("share.medicinal")}</p>
              <ul className="space-y-1.5">
                {id.medicinal_traditional_uses.slice(0, 4).map((u, i) => (
                  <li key={`${String(u).slice(0, 32)}-${i}`} className="text-[12px] text-leaf-ink/85 leading-relaxed rounded-2xl bg-white border border-[#E8E5DF] p-2.5">{u}</li>
                ))}
              </ul>
              <p className="mt-1.5 text-[10px] text-leaf-mist italic">{t("share.medicinalDisclaimer")}</p>
            </div>
          )}
          {isFull && Array.isArray(id.cultural_uses) && id.cultural_uses.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist mb-1">{t("share.cultural")}</p>
              <ul className="space-y-1.5">
                {id.cultural_uses.slice(0, 4).map((u, i) => (
                  <li key={`${String(u).slice(0, 32)}-${i}`} className="text-[12px] text-leaf-ink/85 leading-relaxed rounded-2xl bg-white border border-[#E8E5DF] p-2.5">{u}</li>
                ))}
              </ul>
            </div>
          )}
          {id.notes && (
            <p className="text-[12px] text-leaf-ink/80 leading-relaxed">{id.notes}</p>
          )}
        </div>
      )}
    </article>
  );
};
